/* eslint-disable react/destructuring-assignment */
import { useState, useEffect, useMemo } from 'react';
import { useTable, usePagination, useBlockLayout, Column } from 'react-table';
import { useLocation, useHistory } from 'react-router';
import cn from 'classnames';

import { Bible, PlanEnum, BibleType } from 'api-bible-shared-lib/utils/types';
import Container from '../components/Container';
import { Table, Pagination } from '../components/Table';
import Card from '../components/Card';
import { IconHeadphones, IconBible } from '../components/Icons';
import PageHeader from '../components/PageHeader';
import { usePlans, PlansById } from '../contexts/Plans';

import * as portalApi from '../utils/api';

const planName = (plan: PlanEnum, plansById: PlansById) =>
  Object.keys(plansById).length ? plansById[plan].name ?? '' : '';

const planColor = (plan: PlanEnum, plansById: PlansById) =>
  Object.keys(plansById).length ? plansById[plan].color : '';

const isPlanBible = (plan: PlanEnum, bible: Bible) => {
  if (plan === PlanEnum.FREE || plan === PlanEnum.BASIC) {
    return bible.isOpenAccess || bible.isFairUse;
  }
  if (plan === PlanEnum.PREMIUM) {
    return bible.isOpenAccess || bible.isExpressLicensed;
  }
  return false;
};

const biblePlans = [PlanEnum.FREE, PlanEnum.BASIC, PlanEnum.PREMIUM];

export default function Bibles() {
  const { plansById } = usePlans();

  // bible state
  const [loadedBibles, setLoadedBibles] = useState(false);
  const [bibles, setBibles] = useState<Bible[]>([]);
  const [error, setError] = useState<Error | void>();

  // plan url state
  const history = useHistory();
  const location = useLocation();
  const searchPlans = new URLSearchParams(location.search).get('plan') || '';
  const selectedPlans = useMemo(() => searchPlans.split(',').filter((x) => !!x), [searchPlans]);

  // search / filter bibles state
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<BibleType>('');

  const handlePlanClick = (name: PlanEnum) => {
    let plans: string[] = selectedPlans;

    if (plans.includes(name)) {
      plans = plans.filter((plan) => plan !== name);
    } else {
      plans.push(name);
    }

    history.push({ ...location, search: plans.length ? `?plan=${plans.join(',')}` : '' });
  };

  const [filteredBibles, setFilteredBibles] = useState<Bible[]>([]);

  // handles fetching initial list of bibles
  useEffect(() => {
    portalApi
      .getBibles()
      .then((allBibles) => {
        setBibles(allBibles);
        setFilteredBibles(allBibles);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
        setError(err);
      })
      .finally(() => setLoadedBibles(true));
  }, []);

  // handles search results
  useEffect(() => {
    if (!bibles.length) return;

    const sanitizedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const searchRegex = new RegExp(`\\b${sanitizedQuery}`, 'i');

    setFilteredBibles(
      bibles.filter((b) => {
        const matchesType = typeFilter ? b.type === typeFilter : true;

        const matchesSearch =
          !query ||
          searchRegex.test(b.name) ||
          searchRegex.test(b.id) ||
          searchRegex.test(b.abbreviation || '') ||
          searchRegex.test(b.language || '');

        const isFreeSelected = selectedPlans.includes(PlanEnum.FREE);
        const isBasicSelected = selectedPlans.includes(PlanEnum.BASIC);
        const isPremiumSelected = selectedPlans.includes(PlanEnum.PREMIUM);
        const matchesPlan =
          selectedPlans.length === 0 ||
          ((isFreeSelected || isBasicSelected) &&
            (isPlanBible(PlanEnum.FREE, b) || isPlanBible(PlanEnum.BASIC, b))) ||
          (isPremiumSelected && isPlanBible(PlanEnum.PREMIUM, b));

        return matchesType && matchesSearch && matchesPlan;
      }),
    );
  }, [bibles, query, typeFilter, selectedPlans]);

  const columns = useMemo<Column<Bible>[]>(
    () => [
      {
        accessor: (row) =>
          row.type === 'audio' ? (
            <IconHeadphones className="w-5 text-gray-500" />
          ) : (
            <IconBible className="w-5 text-gray-500" />
          ),
        Header: 'Type',
        width: 48,
      },
      {
        Header: 'Bible Translation',
        accessor: (row) => {
          const hasAbbreviation = !!row.abbreviation;
          const maxLen = hasAbbreviation ? 90 : 80;
          const name = row.name.length > maxLen ? `${row.name.slice(0, maxLen)}...` : row.name;

          return hasAbbreviation ? (
            <>
              {name}{' '}
              <span className="pl-1 font-mono text-xs tracking-wider text-gray-400">
                {row.abbreviation}
              </span>
            </>
          ) : (
            name
          );
        },
      },
      { accessor: 'language', Header: 'Language', width: 200 },
      {
        accessor: (row) => (
          <div className="flex -px-1">
            {biblePlans.map((plan) => (
              <div
                key={plan}
                className="w-3 h-3 mx-0.5 rounded-full"
                style={{
                  background: isPlanBible(plan, row) ? planColor(plan, plansById) : 'transparent',
                }}
              />
            ))}
          </div>
        ),
        Header: 'Plan',
        width: 80,
      },
    ],
    [plansById],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    rows,
    state: { pageIndex },
  } = useTable(
    {
      initialState: { pageSize: 12 },
      columns,
      data: filteredBibles,
    },
    usePagination,
    useBlockLayout,
  );

  const loadedAll = loadedBibles && Object.keys(plansById).length > 0;

  return (
    <Container>
      <PageHeader
        title="Bibles"
        note="Search all Bibles available on API.Bible by Bible name, abbreviation, language, or
        id."
      />
      <Card className="relative px-6 py-8 mb-8 overflow-x-auto md:py-12 md:px-10">
        <div className="flex flex-wrap items-end px-2 mb-4 md:flex-nowrap">
          <div className="w-full pr-3">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700">
              Search
            </label>
            <div className="relative mt-1 rounded-md">
              <div className="absolute inset-y-0 left-0 flex items-center">
                <label htmlFor="bibleType" className="sr-only">
                  Bible Type
                </label>
                <select
                  id="bibleType"
                  name="bibleType"
                  className="h-full py-0 pl-3 text-gray-500 bg-transparent border-transparent focus:ring-blue-500 focus:border-blue-500 pr-7 sm:text-sm rounded-md"
                  onChange={(e) => setTypeFilter(e.target.value as BibleType)}
                >
                  <option value="">All</option>
                  <option value="text">Text</option>
                  <option value="audio">Audio</option>
                </select>
              </div>
              <input
                type="text"
                name="search"
                id="search"
                className="block w-full pl-20 border-gray-300 focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                placeholder="Search (e.g. King James, CEV, Chinese, a81b73293d3080c9-01)"
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>
          <div className="flex mt-4 md:mt-0">
            {biblePlans.map((plan) => (
              <CheckboxButton
                key={plan}
                className="mr-2"
                color={planColor(plan, plansById)}
                checked={selectedPlans.includes(plan)}
                handleChange={() => handlePlanClick(plan)}
              >
                {planName(plan, plansById)}
              </CheckboxButton>
            ))}
          </div>
        </div>
        {!loadedAll && 'Loading...'}

        {loadedAll && error && (
          <h3 className="text-lg text-center text-red-500">Error loading bibles</h3>
        )}
        {loadedAll && !error && (
          <>
            <Table {...getTableProps()} className="w-full mb-6">
              <thead className="text-xs text-gray-400 uppercase">
                {headerGroups.map((headerGroup) => {
                  const headerGroupProps = headerGroup.getHeaderGroupProps();
                  return (
                    <tr
                      {...headerGroupProps}
                      className={cn(headerGroupProps.className, 'flex')}
                      style={{ width: '100%' }}
                    >
                      {headerGroup.headers.map((column, index) => {
                        const headerProps = column.getHeaderProps();
                        const style = index === 1 ? { width: '100%' } : {};
                        return (
                          <th
                            {...headerProps}
                            className={cn(
                              headerProps.className,
                              'p-3 font-normal font-semibold tracking-wider text-left',
                              {
                                'flex-shrink-0 flex-grow-0': index !== 1,
                              },
                            )}
                            style={{ ...headerProps.style, ...style, minWidth: 'unset' }}
                          >
                            {column.render('Header')}
                          </th>
                        );
                      })}
                    </tr>
                  );
                })}
              </thead>
              <tbody
                className="w-full text-gray-900 transition-all ease duration-100"
                {...getTableBodyProps()}
              >
                {page.map((row) => {
                  prepareRow(row);
                  const rowProps = row.getRowProps();
                  return (
                    <tr
                      {...rowProps}
                      className={cn(
                        rowProps.className,
                        'flex py-2 rounded-sm hover:bg-gray-100 transition-all duration-100 mb-.5 items-center overflow-hidden rounded-lg',
                      )}
                      style={{ ...rowProps.style, width: '100%' }}
                    >
                      {row.cells.map((cell, index) => {
                        const cellProps = cell.getCellProps();
                        const style =
                          index !== 1 ? cellProps.style : { ...cellProps.style, width: '100%' };
                        return (
                          <td
                            {...cellProps}
                            className={cn(
                              cellProps.className,
                              'px-3 py-2 whitespace-nowrap text-left truncate',
                              {
                                'flex items-center': index === 0,
                                'flex-shrink-0 flex-grow-0': index !== 1,
                              },
                            )}
                            style={style}
                          >
                            {cell.render('Cell')}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {page.length === 0 && (
                  <tr className="py-2 rounded-sm hover:bg-gray-100 transition-all duration-100 mb-.5 items-center">
                    <td className="w-full px-3 py-2 text-center text-gray-500">
                      No matching results.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            <Pagination
              className="flex justify-center"
              pageCount={pageCount}
              pageIndex={pageIndex}
              gotoPage={gotoPage}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              previousPage={previousPage}
              nextPage={nextPage}
            />
            <div className="mt-6 text-sm text-center text-gray-500">
              {query ? `${rows.length} matching Bibles found` : `${rows.length} available Bibles`}
            </div>
          </>
        )}
      </Card>
    </Container>
  );
}

function CheckboxButton({
  color,
  children,
  className,
  checked,
  handleChange,
}: {
  color: string;
  children: string;
  className: string;
  checked: boolean;
  handleChange: () => void;
}) {
  return (
    <button
      type="button"
      className={cn(
        className,
        'relative flex items-center px-3 py-2 border rounded-md transition-all ease duration-100 outline-none focus:outline-none',
      )}
      style={{ borderColor: checked ? color : 'rgb(209, 213, 219)' }}
      onClick={() => handleChange()}
    >
      <div
        className="absolute top-0 left-0 w-full h-full transition-all ease duration-100"
        style={{ background: color, opacity: checked ? 0.1 : 0 }}
      />
      <div className="relative flex items-center">
        <div className="flex items-center h-5 mr-2">
          <input
            type="checkbox"
            className="w-4 h-4 border-gray-300 rounded cursor-pointer focus:ring-0"
            checked={checked}
            style={{ color }}
            readOnly
          />
        </div>
        <span className="text-sm text-gray-800">{children}</span>
      </div>
    </button>
  );
}
